import { PrintTemplate, TEMPLATE_TYPE_LABELS } from "@/hooks/usePrintTemplates";
import { 
  ensureQZConnected, 
  printReceipt, 
  getPrinterSettings,
  ESCPOS,
  normalizeForPrinter 
} from "@/lib/qz-tray";
import { getPrinterNameForJob } from "@/lib/print-routing";
import { getTemplateFormatting } from "@/lib/print-template-formatting";

export async function printTestReceipt(
  restaurantId: string,
  template: PrintTemplate
): Promise<boolean> {
  const connected = await ensureQZConnected();
  if (!connected) {
    throw new Error("QZ Tray não conectado");
  }

  // Get default printer
  const printerName = await getPrinterNameForJob(restaurantId, "customer_receipt");
  if (!printerName) {
    throw new Error("Nenhuma impressora configurada");
  }

  const printerSettings = await getPrinterSettings(printerName, restaurantId);
  const charsPerLine = printerSettings?.chars_per_line || 48;

  const content = formatTestReceipt(template, charsPerLine);

  return await printReceipt(printerName, content, {
    logType: "teste",
    logIdentifier: "template",
    settings: printerSettings || undefined,
  });
}

function formatTestReceipt(template: PrintTemplate, charsPerLine: number): string {
  const formatting = getTemplateFormatting(template);
  const separator = template.separator_char.repeat(charsPerLine);
  const templateName = TEMPLATE_TYPE_LABELS[template.template_type];
  
  let content = "";

  // Initialize printer
  content += ESCPOS.INIT;

  // Header text if configured
  if (template.header_text) {
    content += ESCPOS.ALIGN_CENTER;
    content += normalizeForPrinter(template.header_text) + "\n";
    content += ESCPOS.ALIGN_LEFT;
  }

  // Title section
  content += separator + "\n";
  content += formatting.title.start;
  content += ESCPOS.ALIGN_CENTER;
  content += normalizeForPrinter("TESTE DE TEMPLATE") + "\n";
  content += normalizeForPrinter(templateName) + "\n";
  content += formatting.title.end;
  content += ESCPOS.ALIGN_LEFT;
  content += separator + "\n";

  // Date/Time
  const now = new Date();
  content += normalizeForPrinter(`Data: ${now.toLocaleDateString("pt-BR")}`);
  content += " ".repeat(charsPerLine - 24);
  content += normalizeForPrinter(`Hora: ${now.toLocaleTimeString("pt-BR")}\n`);
  content += separator + "\n";

  // Content based on template type
  if (template.template_type === "cancellation_receipt") {
    // Cancellation specific test content
    content += normalizeForPrinter("ITEM CANCELADO:\n");
    content += formatting.items.start;
    content += normalizeForPrinter("Picanha 300g\n");
    content += normalizeForPrinter("Qtd: 1\n");
    content += formatting.items.end;
    content += separator + "\n";
    content += formatting.totals.start;
    content += normalizeForPrinter("VALOR: R$ 89,00\n");
    content += formatting.totals.end;
    content += separator + "\n";
    content += normalizeForPrinter("Motivo: Cliente desistiu\n");
    content += normalizeForPrinter("Cancelado por: Maria Santos\n");
    content += separator + "\n";
    content += ESCPOS.ALIGN_CENTER;
    content += normalizeForPrinter("RESPONSAVEL\n\n");
    content += normalizeForPrinter("_".repeat(Math.floor(charsPerLine * 0.6)) + "\n");
    content += normalizeForPrinter("Assinatura\n");
    content += ESCPOS.ALIGN_LEFT;
  } else {
    // Default test content for other templates
    content += normalizeForPrinter("ITENS DE EXEMPLO:\n");
    content += formatting.items.start;
    content += normalizeForPrinter("2x Produto Teste A          R$ 25,00\n");
    content += normalizeForPrinter("1x Produto Teste B          R$ 18,50\n");
    content += normalizeForPrinter("3x Produto Teste C          R$ 42,00\n");
    content += formatting.items.end;
    content += separator + "\n";

    // Totals section
    content += formatting.totals.start;
    const subtotalLine = padLine("Subtotal:", "R$ 85,50", charsPerLine);
    const serviceLine = padLine("Taxa de Servico (10%):", "R$ 8,55", charsPerLine);
    const totalLine = padLine("TOTAL:", "R$ 94,05", charsPerLine);
    content += normalizeForPrinter(subtotalLine);
    content += normalizeForPrinter(serviceLine);
    content += normalizeForPrinter(totalLine);
    content += formatting.totals.end;
  }
  
  content += separator + "\n";

  // Footer section
  content += ESCPOS.ALIGN_CENTER;
  if (template.footer_text) {
    content += normalizeForPrinter(template.footer_text) + "\n";
  }
  if (template.show_software_footer) {
    content += normalizeForPrinter("Altus Food") + "\n";
  }
  content += ESCPOS.ALIGN_LEFT;

  // Configuration summary
  content += separator + "\n";
  content += normalizeForPrinter("CONFIGURACOES APLICADAS:\n");
  content += normalizeForPrinter(`Titulo: ${template.title_bold ? "Negrito" : "Normal"}, ${template.title_size === "double" ? "Grande" : "Normal"}\n`);
  content += normalizeForPrinter(`Itens: ${template.items_bold ? "Negrito" : "Normal"}, ${template.items_size === "double" ? "Grande" : "Normal"}\n`);
  content += normalizeForPrinter(`Totais: ${template.totals_bold ? "Negrito" : "Normal"}, ${template.totals_size === "double" ? "Grande" : "Normal"}\n`);
  content += normalizeForPrinter(`Separador: "${template.separator_char}"\n`);
  content += separator + "\n";

  return content;
}

function padLine(left: string, right: string, width: number): string {
  const spaces = width - left.length - right.length;
  return left + " ".repeat(Math.max(1, spaces)) + right + "\n";
}
